import { UseState } from '@unimpaired/utils'
import * as R from 'ramda'
import React, { isValidElement } from 'react'
import { twMerge } from 'tailwind-merge'
import { Modal, ModalType } from '../Modal'
import {
  DayScheduleProps,
  ScheduleEvent,
  ScheduleModalState,
} from './DaySchedule.types'
import { Agenda } from './Agenda'
import {
  isPlainModalContent,
  isScheduleEventList,
} from './schedule.modal.helpers'

export const DayScheduleEventDetailsModal = <T extends ScheduleEvent>(
  props: {
    eventDisplayedState: UseState<T | undefined>
    modalState: UseState<ScheduleModalState<T>>
  } & Pick<
    DayScheduleProps<T>,
    | 'modalType'
    | 'renderModalContent'
    | 'eventDescriptor'
    | 'onCreateNew'
    | 'timezone'
  >,
) => {
  const [eventDisplayed, setEventDisplayed] = props.eventDisplayedState
  const [modal, setModal] = props.modalState

  // Custom modal content whose body is a React element already handles its
  // own padding. Only pad when the modal is showing one of the other shapes.
  const contentRendersItsOwnPadding =
    isPlainModalContent(modal.content) && isValidElement(modal.content.content)
  const padModalContent = !!modal.content && !contentRendersItsOwnPadding

  const getModalTitle = () => {
    if (!modal.content) return undefined
    if (isPlainModalContent(modal.content)) return modal.content.title ?? undefined
    if (modal.content.length === 1) return modal.content[0].name
    return `${modal.content.length} Events`
  }

  const getModalType = (): ModalType => {
    if (!props.modalType) return 'modal'
    if (typeof props.modalType === 'string') return props.modalType
    if (!eventDisplayed) return 'modal'
    return props.modalType(eventDisplayed)
  }

  // prettier-ignore
  const omittedProps = [ 'condensed', 'modalContent', 'eventDescriptor', 'onClick', 'colStart', 'colWidth' ]

  return (
    <Modal
      type={getModalType()}
      className={twMerge(padModalContent && 'p-4')}
      title={getModalTitle()}
      isOpen={modal.open || !!eventDisplayed}
      onClose={() => {
        setEventDisplayed(undefined)
        setModal((modal) => ({ ...modal, open: false }))
      }}>
      {/*
        Three paths, in priority order:
        1. `renderModalContent` prop + a clicked event  -> render the custom body
        2. modal.content is PlainModalContent           -> render its `.content`
        3. modal.content is ScheduleEvent[]             -> render an Agenda of those events
        If an event is clicked with none of the above, the caller has already
        rendered a default body before this component is mounted.
      */}
      {eventDisplayed &&
        props.renderModalContent &&
        // @ts-expect-error DayScheduleEvent internal props are stripped by omit
        props.renderModalContent(R.omit(omittedProps, eventDisplayed), () =>
          setEventDisplayed(undefined),
        )}
      {isPlainModalContent(modal.content) && modal.content.content}

      {isScheduleEventList(modal.content) && (
        <Agenda events={modal?.content || []} timezone={props.timezone} />
      )}
    </Modal>
  )
}
